import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function UpgradeDialog({ open, onOpenChange, server }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      (async () => {
        const { data } = await api.get("/payments/packages");
        setPackages(data);
      })();
    }
  }, [open]);

  const startCheckout = async (packageId) => {
    if (!server) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: packageId,
        server_id: server.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0F] border-white/10 text-white w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:max-w-lg grid-cols-[minmax(0,1fr)]"
        data-testid="upgrade-dialog"
      >
        <DialogHeader className="min-w-0">
          <DialogTitle className="font-heading text-xl sm:text-2xl flex items-start gap-2 min-w-0 pr-8 [overflow-wrap:anywhere]">
            <Sparkles className="w-5 h-5 text-neon-cyan shrink-0 mt-1" />
            <span className="min-w-0 [overflow-wrap:anywhere]">Feature {server?.name}</span>
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Pin your server to the top of the listings and unlock the neon glow.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-3">
          {packages.map((p, i) => (
            <button
              key={p.id}
              disabled={loading}
              data-testid={`buy-${p.id}`}
              onClick={() => startCheckout(p.id)}
              className={`w-full text-left rounded-xl p-4 border transition-all ${
                i === 1 ? "border-blurple bg-blurple/10 hover:border-blurple hover:bg-blurple/15" : "border-white/10 bg-white/5 hover:bg-white/10"
              } disabled:opacity-50`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-bold">{p.label}</div>
                  <div className="text-xs text-zinc-400 mt-1">Pinned to top for {p.days} days</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-2xl font-black">${p.amount.toFixed(2)}</div>
                  {i === 1 && (
                    <div className="text-[10px] uppercase tracking-widest text-neon-cyan mt-1 inline-flex items-center gap-0.5">
                      <Check className="w-3 h-3" /> Best value
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>

        {loading && (
          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-zinc-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Redirecting to Stripe...
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
